/**
 * Eurocode-style display notation: turn underscores into HTML <sub> or React fragments.
 * Does not change API keys — use only for user-visible strings.
 */

import React from 'react';

export function escapeHtml(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Eurocode-style: leading capital (optional short lowercase tail) or λ + _ + subscript.
 * Avoids matching snake_case keys like shear_capacity.
 */
const EUROCODE_SUBSCRIPT_RE =
  /([A-Za-z][a-z]{0,3}|[\u03bb\u039b])_([A-Za-z0-9]+(?:,[A-Za-z0-9]+)*)/g;

const SPECIAL_SUB_PLACEHOLDERS = [
  { re: /\|My_ed\|\/My_rd/g, html: '|M<sub>y,Ed</sub>|/M<sub>y,Rd</sub>' },
  { re: /\|Mz_ed\|\/Mz_rd/g, html: '|M<sub>z,Ed</sub>|/M<sub>z,Rd</sub>' },
];

function insertSpecialSubPlaceholder(str) {
  let s = String(str);
  const placeholders = [];
  SPECIAL_SUB_PLACEHOLDERS.forEach((spec, i) => {
    const ph = `\uE000${i}\uE001`;
    placeholders[i] = spec.html;
    s = s.replace(spec.re, ph);
  });
  return { s, placeholders };
}

function resolveSubPlaceholders(joined, placeholders) {
  let out = joined;
  placeholders.forEach((html, i) => {
    if (!html) return;
    const ph = `\uE000${i}\uE001`;
    out = out.split(ph).join(html);
  });
  return out;
}

/**
 * Insert <sub>…</sub> for Eurocode patterns; escape the rest.
 * @param {string|null|undefined} str
 * @returns {string}
 */
export function eurocodeToHtmlSubscripts(str) {
  if (str == null || str === '') return '';
  const { s: withPh, placeholders } = insertSpecialSubPlaceholder(str);
  const parts = [];
  let last = 0;
  const re = new RegExp(EUROCODE_SUBSCRIPT_RE.source, 'g');
  let m;
  while ((m = re.exec(withPh)) !== null) {
    parts.push(escapeHtml(withPh.slice(last, m.index)));
    parts.push(`${escapeHtml(m[1])}<sub>${escapeHtml(m[2])}</sub>`);
    last = m.index + m[0].length;
  }
  parts.push(escapeHtml(withPh.slice(last)));
  return resolveSubPlaceholders(parts.join(''), placeholders);
}

/**
 * Parse a plain string into React children with <sub> for Eurocode patterns.
 * @param {string} str
 * @returns {React.ReactNode[]}
 */
export function parseEurocodeToReact(str) {
  if (str == null || str === '') return [];
  const s = String(str);
  const out = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(EUROCODE_SUBSCRIPT_RE.source, 'g');
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) {
      out.push(s.slice(last, m.index));
    }
    out.push(
      React.createElement(
        React.Fragment,
        { key: `ec-${key++}` },
        m[1],
        React.createElement('sub', null, m[2]),
      ),
    );
    last = m.index + m[0].length;
  }
  if (last < s.length) {
    out.push(s.slice(last));
  }
  return out;
}

/**
 * @param {object} props
 * @param {string} props.children
 * @param {React.ElementType} [props.component]
 */
export function EurocodeText({ children, component: Comp = 'span', ...rest }) {
  const s = typeof children === 'string' ? children : '';
  const nodes = parseEurocodeToReact(s);
  return <Comp {...rest}>{nodes}</Comp>;
}

export default eurocodeToHtmlSubscripts;
